import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useEncryption } from '@/hooks/useEncryption';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { useGoogleDrive } from '@/hooks/useGoogleDrive';
import { useRepository } from '@/contexts/PlatformContext';
import { gatherBackupPayload, type ManuscriptBackupPayload } from "@/lib/backupPayload";
import type { Manuscript } from "@/types/database";
import { processPayload } from "@/lib/processPayload";
import { logger } from "@/lib/logger";
import { getSha256, stringifyCanonicalJson } from "@/utils/hash";
import { computeEntityHashes } from "@/lib/smartMerge";
import { getLastSyncedKey, getLastSyncedHashKey, getEntityHashesKey } from "@/utils/storageKeys";

export function useRestoreBackup() {
  const { user } = useAuth();
  const { isReady, decryptContent, encryptContent } = useEncryption();
  const queryClient = useQueryClient();
  const repository = useRepository();

  const {
    isConnected: isGoogleConnected,
    listDriveManuscripts,
    pullFromDrive,
    getAccessToken,
    getAppFolderId,
    findFileByManuscriptId,
    uploadManuscript,
    getLegacyFolderId,
    downloadManuscript,
  } = useGoogleDrive();

  const [isRestoring, setIsRestoring] = useState(false);
  const [isRestoringDrive, setIsRestoringDrive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Restores all backups associated with the user from Supabase
  const restoreAllFromSupabase = useCallback(async () => {
    logger.log("[Restore] Starting restoreAllFromSupabase callback...", { userId: user?.id });
    if (!user?.id) throw new Error("Not authenticated");
    
    setIsRestoring(true);
    setError(null);
    
    try {
      logger.log("[Restore] Making Supabase query to manuscript_backups...");
      const { data, error: fetchError } = await supabase
        .from('manuscript_backups')
        .select('*')
        .eq('user_id', user.id);
        
      logger.log("[Restore] Supabase query returned:", { dataCount: data?.length, fetchError });
        
      if (fetchError || !data || data.length === 0) {
        throw new Error(fetchError?.message || "No backups found in cloud storage.");
      }

      let restoredCount = 0;
      const failedIds: string[] = [];
      logger.log(`[Restore] Processing ${data.length} backups...`);

      for (const row of data) {
        // Skip internal sentinel rows (e.g. __verification__ used by encryption setup)
        if (row.manuscript_id.startsWith('__')) continue;

        let payloadStr = row.content;

        if (row.is_encrypted) {
          logger.log(`[Restore] Decrypting backup for manuscript ${row.manuscript_id}...`);
          const decrypted = await decryptContent(row.content);
          if (decrypted) {
            payloadStr = decrypted;
          } else {
            // Fallback: check if content is actually unencrypted JSON
            try {
              JSON.parse(row.content);
              payloadStr = row.content;
            } catch {
              console.error(`[Restore] Failed to decrypt manuscript ${row.manuscript_id}. Skipping.`);
              failedIds.push(row.manuscript_id);
              continue;
            }
          }
        }

        try {
          const payload: ManuscriptBackupPayload = JSON.parse(payloadStr);
          logger.log(`[Restore] Processing payload for manuscript ${row.manuscript_id}...`);
          await processPayload(payload, repository);

          // Save sync metadata so future syncs have a valid baseline
          const payloadHash = await getSha256(stringifyCanonicalJson(payload));
          const entityHashes = await computeEntityHashes(payload);
          const serverUpdatedAt = row.updated_at ? new Date(row.updated_at).toISOString() : new Date().toISOString();

          localStorage.setItem(getLastSyncedKey(row.manuscript_id), serverUpdatedAt);
          localStorage.setItem(getLastSyncedHashKey(row.manuscript_id), payloadHash);
          localStorage.setItem(getEntityHashesKey(row.manuscript_id), JSON.stringify(entityHashes));

          restoredCount++;
        } catch (parseErr) {
          console.error(`[Restore] Failed to parse payload for manuscript ${row.manuscript_id}`, parseErr);
          failedIds.push(row.manuscript_id);
        }
      }

      if (failedIds.length > 0) {
        console.warn(`[Restore] ${failedIds.length} manuscript(s) could not be restored:`, failedIds);
      }

      if (restoredCount === 0 && failedIds.length > 0) {
        throw new Error(`All ${failedIds.length} backup(s) failed to decrypt or parse. Check your encryption passphrase.`);
      }
      
      logger.log("[Restore] Invalidating React Query caches...");
      queryClient.invalidateQueries();
      logger.log("[Restore] Caches invalidated. Restore complete!");
      
      return restoredCount;
    } catch (err) {
      console.error("[Restore] Failed to restore all backups:", err);
      setError(err instanceof Error ? err.message : "Restore failed");
      throw err;
    } finally {
      logger.log("[Restore] Exiting restoreAllFromSupabase callback, setting isRestoring to false.");
      setIsRestoring(false);
    }
  }, [user?.id, decryptContent, queryClient]);

  // Restores all backups from Google Drive
  const restoreAllFromGoogleDrive = useCallback(async () => {
    if (!user?.id) throw new Error("Not authenticated");
    if (!isGoogleConnected) throw new Error("Google Drive is not connected");

    setIsRestoringDrive(true);
    setError(null);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error("Failed to get access token");

      logger.log("[Restore] Fetching file list from namespaced Google Drive folder...");
      const driveFiles = await listDriveManuscripts();
      logger.log("[Restore] Found namespaced Google Drive files:", driveFiles.length);

      const foundIds = new Set<string>();
      for (const file of driveFiles) {
        const idMatch = file.name.match(/^(.*)\.json$/);
        if (idMatch) foundIds.add(idMatch[1]);
      }

      // Check legacy folder for fallback migration
      const legacyFolderId = await getLegacyFolderId(accessToken);
      const legacyFiles: Array<{ id: string; name: string }> = [];
      if (legacyFolderId) {
        logger.log("[Restore] Legacy folder 'Deckle' found. Scanning for fallback backups...");
        const rawLegacyFiles = await listDriveManuscripts(legacyFolderId);
        
        for (const file of rawLegacyFiles) {
          const idMatch = file.name.match(/^(.*)\.json$/);
          if (idMatch && !foundIds.has(idMatch[1])) {
            legacyFiles.push(file);
          }
        }
        logger.log(`[Restore] Found ${legacyFiles.length} additional backups in legacy folder.`);
      }

      const allFiles = [...driveFiles, ...legacyFiles];
      logger.log("[Restore] Total Google Drive files to restore:", allFiles.length);

      let restoredCount = 0;
      for (const file of allFiles) {
        const idMatch = file.name.match(/^(.*)\.json$/);
        if (!idMatch) continue;

        const manuscriptId = idMatch[1];
        logger.log(`[Restore] Pulling file: ${file.name} (id: ${manuscriptId}, fileId: ${file.id})...`);
        const driveData = await downloadManuscript(accessToken, file.id);
        if (!driveData) {
          console.warn(`[Restore] Failed to download data for manuscript ${manuscriptId}`);
          continue;
        }

        let payload: ManuscriptBackupPayload;
        let needsReupload = false;
        const isFromLegacy = legacyFiles.some(lf => lf.id === file.id);

        if ('schemaVersion' in driveData && driveData.schemaVersion === 2 && driveData.payload) {
          payload = driveData.payload;
          if (driveData.userId !== user.id || isFromLegacy) {
            needsReupload = true;
          }
        } else {
          // Construct payload from V1/Legacy format
          const rawData = driveData as Record<string, unknown>;
          payload = {
            manuscript: {
              id: driveData.id,
              title: driveData.title,
              content: (rawData.content as string) || "",
              wordCount: driveData.wordCount,
              lastModified: new Date(driveData.lastModified),
              authorName: driveData.metadata?.authorName,
              defaultPOV: driveData.metadata?.defaultPOV as Manuscript['defaultPOV'],
              defaultTense: driveData.metadata?.defaultTense as Manuscript['defaultTense'],
              type: "standalone",
            },
            plannerStructure: undefined,
            codexEntries: [],
            goals: undefined,
            formatSettings: undefined,
            sceneContent: [],
          };
          needsReupload = true;
        }

        logger.log(`[Restore] Restoring manuscript ${manuscriptId} into DataRepository...`);
        await processPayload(payload, repository);

        // Reflect on Supabase: encrypt and upload
        logger.log(`[Restore] Reflecting manuscript ${manuscriptId} on Supabase...`);
        const payloadJson = JSON.stringify(payload);
        let contentToStore = payloadJson;
        let isEncrypted = false;

        if (isReady) {
          const encrypted = await encryptContent(payloadJson);
          if (encrypted) {
            contentToStore = encrypted;
            isEncrypted = true;
          }
        }

        const { error: supabaseError } = await supabase
          .from('manuscript_backups')
          .upsert(
            {
              user_id: user.id,
              manuscript_id: manuscriptId,
              content: contentToStore,
              is_encrypted: isEncrypted,
            },
            { onConflict: 'user_id,manuscript_id' }
          );

        if (supabaseError) {
          console.error(`[Restore] Failed to sync ${manuscriptId} to Supabase:`, supabaseError);
        }

        // Update ownership and format on Google Drive in our namespaced folder if needed
        if (needsReupload && payload.manuscript) {
          logger.log(`[Restore] Re-uploading amended manuscript ${manuscriptId} to namespaced Google Drive folder...`);
          const folderId = await getAppFolderId(accessToken);
          if (folderId) {
            const existingFile = await findFileByManuscriptId(accessToken, folderId, manuscriptId);
            
            const manuscriptObj: Manuscript = {
              id: payload.manuscript.id,
              title: payload.manuscript.title,
              type: payload.manuscript.type || "standalone",
              wordCount: payload.manuscript.wordCount,
              lastModified: new Date(payload.manuscript.lastModified),
              authorName: payload.manuscript.authorName,
              defaultPOV: payload.manuscript.defaultPOV,
              defaultTense: payload.manuscript.defaultTense,
              seriesId: payload.manuscript.seriesId,
              seriesOrder: payload.manuscript.seriesOrder,
              collectionId: payload.manuscript.collectionId,
            };

            await uploadManuscript(accessToken, folderId, manuscriptObj, existingFile?.id);
          }
        }

        restoredCount++;
      }

      logger.log("[Restore] Invalidating React Query caches...");
      queryClient.invalidateQueries();
      logger.log("[Restore] Caches invalidated. Google Drive restore complete!");

      return restoredCount;
    } catch (err) {
      console.error("[Restore] Failed to restore from Google Drive:", err);
      setError(err instanceof Error ? err.message : "Google Drive restore failed");
      throw err;
    } finally {
      setIsRestoringDrive(false);
    }
  }, [
    user?.id,
    isGoogleConnected,
    listDriveManuscripts,
    getAccessToken,
    getAppFolderId,
    findFileByManuscriptId,
    uploadManuscript,
    getLegacyFolderId,
    downloadManuscript,
    isReady,
    encryptContent,
    queryClient,
  ]);

  const restoreFromBirchFile = useCallback(async (file: File): Promise<string> => {
    setIsRestoring(true);
    setError(null);
    try {
      const text = await file.text();
      let payload: ManuscriptBackupPayload | null = null;
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === 'object' && parsed.manuscript) {
          payload = parsed as ManuscriptBackupPayload;
        }
      } catch {
        // Text is encrypted, continue to decryption below
      }

      if (!payload && isReady) {
        const decrypted = await decryptContent(text);
        if (decrypted) {
          payload = JSON.parse(decrypted) as ManuscriptBackupPayload;
        }
      }

      if (!payload || !payload.manuscript || !payload.manuscript.title) {
        throw new Error("Invalid Birch backup file format.");
      }
      await processPayload(payload);
      queryClient.invalidateQueries();
      return payload.manuscript.title;
    } catch (err) {
      console.error("[Restore] Failed to restore .birch file:", err);
      const msg = err instanceof Error ? err.message : "Failed to restore .birch file.";
      setError(msg);
      throw new Error(msg);
    } finally {
      setIsRestoring(false);
    }
  }, [isReady, decryptContent, queryClient]);

  return { 
    restoreAllFromSupabase, 
    restoreAllFromGoogleDrive,
    restoreFromBirchFile,
    isRestoring, 
    isRestoringDrive,
    error 
  };
}